import type { ToolbarProps, View } from 'react-big-calendar'
import React from 'react'
import {
  makeStyles,
  Typography,
  Button,
  Hidden,
  IconButton,
  SelectableChipSmall,
  Skeleton,
  Spinner,
  useIsMobile
} from '@paintscout/ui'
import { SingleDatePicker, useUser, useUsers } from '@paintscout/ui-business'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import classnames from 'classnames'
import moment from 'moment'
import type { CalendarEvent } from 'paintscout'

const SPACE_BETWEEN = 'space-between' as const

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: SPACE_BETWEEN,
    padding: theme.spacing(1.5, 2, 1, 2),
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    marginBottom: theme.spacing(0.5)
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1)
    }
  },
  navigation: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  navigationText: {
    ...theme.typography.body1,
    textTransform: 'none',
    color: theme.paintscout.palette.brand.Neutral.P50,
    [theme.breakpoints.down('xs')]: {
      fontSize: '12px'
    }
  },
  boldNavigationText: {
    fontWeight: 600
  },
  clickableNavigation: {
    cursor: 'pointer',
    '&:hover': {
      '& > *': {
        color: `${theme.paintscout.palette.primary} !important`
      }
    }
  },
  mobileNavigation: {
    display: 'flex',
    justifyContent: SPACE_BETWEEN,
    alignItems: 'center',

    '& > div': {
      width: 'fit-content'
    }
  },
  loadingSpinner: {
    marginRight: theme.spacing(2),
    '& div': {
      width: '20px !important',
      height: '20px !important'
    }
  },
  productionNavigation: {
    display: 'flex',
    justifyContent: SPACE_BETWEEN,
    alignItems: 'center',
    padding: theme.spacing(1),
    paddingTop: 0,
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  }
}))

interface CalendarToolbarProps extends ToolbarProps {
  events: CalendarEvent[]
  date: Date
  setDate: (date: Date) => void
  tab: 'calendar' | 'map' | 'schedule'
  view: View
  setView: (view: View) => void
  fetchingIntegrationEvents?: boolean
  loading?: boolean
  hideWeekends?: boolean
  EstimatorSelectComponent: React.ReactNode
  isProduction?: boolean
}

export default function CalendarToolbar(props: CalendarToolbarProps) {
  const {
    view: contextView,
    onView,
    label,
    events,
    date,
    setDate,
    tab,
    setView,
    loading,
    fetchingIntegrationEvents,
    hideWeekends,
    EstimatorSelectComponent,
    isProduction
  } = props

  const { users } = useUsers()
  const classes = useStyles()
  const { isSales } = useUser()
  const mobile = useIsMobile({ xs: true })

  const isProductionMap = isProduction && tab === 'map'
  const view = isProductionMap ? 'day' : contextView
  const showEstimatorSelect = users?.length > 1 && !loading && !isSales && view !== 'agenda'

  const getMapLabel = () => {
    if (view === 'day') {
      return moment(date).format('dddd MMMM D, YYYY')
    }

    if (view === 'week') {
      const start = moment(date).clone().startOf('week')
      const end = moment(date).clone().endOf('week')
      if (start.month() === end.month()) {
        return `${start.format('MMMM DD')} – ${end.format('DD')}`
      }
      return `${start.format('MMMM DD')} – ${end.format('MMMM DD')}`
    }

    return moment(date).format('MMMM YYYY')
  }

  const constructLabel = () => {
    if (tab === 'map') {
      return getMapLabel()
    }

    if (view === 'week' && hideWeekends) {
      const mondayOfThisWeek = moment(date).startOf('week').add(1, 'days')
      const fridayOfThisWeek = moment(date).endOf('week').subtract(1, 'days')
      const firstMonth = mondayOfThisWeek.format('MMMM')
      const lastMonth = fridayOfThisWeek.format('MMMM')
      if (firstMonth === lastMonth) {
        return `${firstMonth} ${mondayOfThisWeek.format('DD')} – ${fridayOfThisWeek.format('DD')}`
      } else {
        return `${firstMonth} ${mondayOfThisWeek.format('DD')} – ${lastMonth} ${fridayOfThisWeek.format('DD')}`
      }
    }
    if (view === 'day') {
      return moment(date).format(mobile ? 'dddd, MMM D' : 'dddd, MMMM D, YYYY')
    }

    if (view === 'month') {
      return moment(date).format('MMMM YYYY')
    }

    return label
  }

  const formatLabel = () => {
    return constructLabel()
      .split(' ')
      .map((word, index) => {
        const boldWord = word.length > 4 || index === 0 || (index === 1 && view === 'day')
        if (boldWord) {
          return (
            <span className={classnames(classes.navigationText, classes.boldNavigationText)} key={index}>
              {word}{' '}
            </span>
          )
        } else {
          return (
            <span className={classes.navigationText} key={index}>
              {word}{' '}
            </span>
          )
        }
      })
  }

  // Map view to valid moment.js units
  const getMomentUnit = (view: string) => {
    switch (view) {
      case 'day':
        return 'day'
      case 'week':
      case 'work_week':
        return 'week'
      case 'month':
        return 'month'
      default:
        return 'day'
    }
  }

  const handleNavigate = (action: string, specificDate?: Date) => {
    const unit = getMomentUnit(view)
    switch (action) {
      case 'PREV':
        setDate(moment(date).subtract(1, unit).toDate())
        break
      case 'NEXT':
        setDate(moment(date).add(1, unit).toDate())
        break
      case 'TODAY':
        setDate(moment().toDate())
        break
      case 'DATE':
        setDate(moment(specificDate).toDate())
        break
      default:
        break
    }
  }

  const handleViewChange = (nextView: View) => {
    if (tab === 'map') {
      if (view !== nextView) {
        setView(nextView)
      }
      return
    }

    onView(nextView)
  }

  const NavigationComponent = () => {
    if (loading && !mobile) {
      return (
        <div className={classes.navigation}>
          <Skeleton variant="rect" width={160} height={32} />
        </div>
      )
    }

    return (
      <>
        <IconButton size="small" onClick={() => handleNavigate('PREV')}>
          <ChevronLeftIcon />
        </IconButton>
        <SingleDatePicker
          date={moment(date)}
          onChange={(date) => handleNavigate('DATE', date.toDate())}
          events={events.filter((event) => event.eventType !== '_unavailable_')}
          customTrigger={
            <button>
              <Typography className={classes.clickableNavigation}>{formatLabel()}</Typography>
            </button>
          }
          PopperProps={{
            placement: 'bottom-end'
          }}
          modalBreakpoint={null}
        />
        <IconButton size="small" onClick={() => handleNavigate('NEXT')}>
          <ChevronRightIcon />
        </IconButton>
      </>
    )
  }

  const TodayComponent = () => {
    if (loading) {
      return <Skeleton variant="rect" width={64} height={32} className={classes.clickableNavigation} />
    }
    return (
      <Button
        className={classnames(classes.navigationText, classes.boldNavigationText, classes.clickableNavigation)}
        variant="text"
        size="small"
        onClick={() => handleNavigate('TODAY')}
      >
        Today
      </Button>
    )
  }

  return (
    <>
      <div className={classes.root}>
        {isProductionMap ? (
          <Typography variant="caption" color="textSecondary">
            Viewing by day
          </Typography>
        ) : (
          <div className={classes.buttonContainer}>
            {!loading && (
              <>
                {isProduction && (
                  <SelectableChipSmall
                    label="Schedule"
                    selected={view === 'agenda'}
                    onClick={() => handleViewChange('agenda')}
                  />
                )}

                <SelectableChipSmall label="Day" selected={view === 'day'} onClick={() => handleViewChange('day')} />
                <SelectableChipSmall label="Week" selected={view === 'week'} onClick={() => handleViewChange('week')} />
                <SelectableChipSmall
                  label="Month"
                  selected={view === 'month'}
                  onClick={() => handleViewChange('month')}
                />
              </>
            )}
          </div>
        )}

        <div>
          <div className={classes.navigation}>
            <Hidden xsDown>
              {fetchingIntegrationEvents && <Spinner className={classes.loadingSpinner} />}
              {view !== 'agenda' && <TodayComponent />}
              {view !== 'agenda' && <NavigationComponent />}
            </Hidden>
          </div>
        </div>
      </div>
      {showEstimatorSelect && EstimatorSelectComponent}
      {!loading && (
        <Hidden smUp>
          {view !== 'month' && view !== 'agenda' && (
            <div className={classes.productionNavigation}>
              <TodayComponent />
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <NavigationComponent />
              </div>
            </div>
          )}
        </Hidden>
      )}

      {/* {isMobileProduction && view !== 'month' && view !== 'agenda' && (
        <div className={classes.productionNavigation} style={tab === 'map' ? { marginTop: 8 } : {}}>
          <TodayComponent />
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <NavigationComponent />
          </div>
        </div>
      )} */}
    </>
  )
}
